import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Users, Clock, CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react'
import { Card, CardContent, Button, Badge } from '@/components/ui'
import { LoadingSpinner } from '@/components/shared'
import { useAuthStore } from '@/stores'
import { familyApi } from '@/lib/api'

type ActionState = 'idle' | 'accepting' | 'accepted' | 'error' | 'already_member'

export function AcceptInvitePage() {
  const { invitationId } = useParams<{ invitationId: string }>()
  const navigate = useNavigate()
  const { user, tokens } = useAuthStore()
  const isAuthenticated = !!tokens

  const [actionState, setActionState] = useState<ActionState>('idle')
  const [actionError, setActionError] = useState<string | null>(null)

  // Fetch invitation details using react-query
  const {
    data: invitation,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['invitation', invitationId],
    queryFn: () => familyApi.getInvitationDetails(invitationId!),
    enabled: !!invitationId,
    retry: false,
  })

  const handleAccept = async () => {
    if (!isAuthenticated || !invitationId) return

    try {
      setActionState('accepting')
      await familyApi.acceptInvitation(invitationId)
      setActionState('accepted')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation'
      if (errorMessage.includes('already a member')) {
        setActionState('already_member')
      } else {
        setActionState('error')
        setActionError(errorMessage)
      }
    }
  }

  const handleSignIn = () => {
    navigate(`/login?redirect=/invite/${invitationId}`)
  }

  const handleSignUp = () => {
    const params = new URLSearchParams({
      redirect: `/invite/${invitationId}`,
    })
    if (invitation?.email) {
      params.set('email', invitation.email)
    }
    navigate(`/signup?${params.toString()}`)
  }

  const handleGoToDashboard = () => {
    navigate('/')
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Determine display state
  const getDisplayState = () => {
    if (actionState !== 'idle') return actionState
    if (fetchError || !invitationId) return 'error'
    if (!invitation) return 'error'
    if (invitation.is_expired) return 'expired'
    if (!invitation.is_valid) {
      if (invitation.status === 'ACCEPTED') return 'accepted'
      return 'error'
    }
    return 'view'
  }

  const displayState = getDisplayState()
  const displayError = actionError || (fetchError instanceof Error ? fetchError.message : 'Failed to load invitation')

  return (
    <div className="hearth-canvas min-h-screen py-12">
      <div className="mx-auto max-w-md px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary-600">FamSteady</h1>
          <p className="mt-1 text-sm text-gray-600">Peace of mind for family care</p>
        </div>

        {/* Invitation Card */}
        <Card>
          <CardContent className="p-6">
            {/* Error State */}
            {displayState === 'error' && (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-error-100">
                  <XCircle className="h-8 w-8 text-error-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Something went wrong</h2>
                <p className="mt-2 text-gray-600">{displayError}</p>
                <Button onClick={() => navigate('/login')} className="mt-6">
                  Go to Login
                </Button>
              </div>
            )}

            {/* Expired State */}
            {displayState === 'expired' && invitation && (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-warning-100">
                  <AlertTriangle className="h-8 w-8 text-warning-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Invitation Expired</h2>
                <p className="mt-2 text-gray-600">
                  This invitation has expired. Please contact{' '}
                  <span className="font-medium">{invitation.invited_by_name}</span> to send a new one.
                </p>
                <div className="mt-4 rounded-lg bg-gray-50 p-4 text-left">
                  <p className="text-sm text-gray-500">Invitation details:</p>
                  <p className="mt-1 font-medium">{invitation.family_name}</p>
                  <p className="text-sm text-gray-600">Role: {invitation.role_display}</p>
                </div>
                <Button variant="outline" onClick={() => navigate('/login')} className="mt-6">
                  Go to Login
                </Button>
              </div>
            )}

            {/* Already Member State */}
            {displayState === 'already_member' && (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                  <Users className="h-8 w-8 text-primary-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">You're already a member!</h2>
                <p className="mt-2 text-gray-600">
                  You're already part of <span className="font-medium">{invitation?.family_name}</span>.
                </p>
                <Button onClick={handleGoToDashboard} className="mt-6">
                  Go to Dashboard
                </Button>
              </div>
            )}

            {/* View State (show invitation details) */}
            {displayState === 'view' && invitation && (
              <>
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                    <Users className="h-8 w-8 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">You're Invited!</h2>
                  <p className="mt-2 text-gray-600">
                    <span className="font-medium">{invitation.invited_by_name}</span> has invited you to
                    join their family on FamSteady.
                  </p>
                </div>

                {/* Invitation Details */}
                <div className="mt-6 rounded-lg bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Family</p>
                      <p className="font-semibold text-gray-900">{invitation.family_name}</p>
                    </div>
                    <Badge variant="secondary">{invitation.role_display}</Badge>
                  </div>

                  {invitation.message && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <p className="text-sm text-gray-500">Personal message</p>
                      <p className="mt-1 text-gray-700 italic">"{invitation.message}"</p>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>
                      Expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Invited Email Notice */}
                <div className="mt-4 rounded-lg bg-info-50 p-3">
                  <p className="text-sm text-info-700">
                    This invitation was sent to <span className="font-medium">{invitation.email}</span>
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-6 space-y-3">
                  {isAuthenticated ? (
                    <>
                      <Button onClick={handleAccept} className="w-full" disabled={actionState === 'accepting'}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Accept Invitation
                      </Button>
                      {user?.email && user.email.toLowerCase() !== invitation.email.toLowerCase() && (
                        <p className="text-center text-xs text-gray-500">
                          You're signed in as <span className="font-medium">{user.email}</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <Button onClick={handleSignIn} className="w-full">
                        Sign In to Accept
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <Button variant="outline" onClick={handleSignUp} className="w-full">
                        Create Account
                      </Button>
                      <p className="text-center text-xs text-gray-500">
                        All sign-in methods available (email, Google, Apple)
                      </p>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Accepting State */}
            {displayState === 'accepting' && (
              <div className="py-8 text-center">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-gray-600">Joining family...</p>
              </div>
            )}

            {/* Accepted State */}
            {displayState === 'accepted' && (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-100">
                  <CheckCircle className="h-8 w-8 text-success-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Welcome to the family!</h2>
                <p className="mt-2 text-gray-600">
                  You've successfully joined <span className="font-medium">{invitation?.family_name}</span>{' '}
                  as a <span className="font-medium">{invitation?.role_display}</span>.
                </p>
                <Button onClick={handleGoToDashboard} className="mt-6">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Questions?{' '}
            <Link to="/support" className="text-primary-600 hover:text-primary-700">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
